import React, { useEffect } from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { Cell, Grid } from 'styled-css-grid';

import Form, { FormField, formSelector } from '@xcritical/forms';

import { Spacing } from '@crm-framework/spacing';
import {
  registrySelector,
  IStoreWithRegistryState,
} from '@crm-framework/registry';
import {
  InputFormField,
  DatePickerFormField,
  TextareaFormField,
} from '@crm-framework/form-fields';
import { checkRight, IStoreWithAuth } from '@crm-framework/auth';
import { Label } from '@crm-framework/label';

import { DictionarySelect } from '@crm-package/dictionaries';
import { formDatePickerFormat } from '@crm-package/app';
import { USER_RIGHTS } from '@crm-package/auth';

import { promoActions } from './actions';
import { BrandLink } from './components/Link';
import { Rules } from './components/Rules';
import { Limitation } from './components/Limitation';
import { Description } from './components/description';
import { IPromoPreviewProps } from './types';
import { PromoFormActions } from './components/PromoFormActions';
import { PromoSkeleton } from './components/PromoSkeleton';
import { ErrorMessageWrapper } from './components/styled';
import { isLeadPromo, isNewPromo } from './utils';
import {
  PROMO_FORM_NAME,
  PROMO_REGISTRIES,
  PROMO_TYPE,
  TRANSACTION_SUB_TYPE_CREDIT_DICTIONARY_NAME,
} from './consts';

const errorProps = {
  shouldFitContainer: false,
};

export const PromoPreview: React.FC<IPromoPreviewProps> = ({
  id,
  location,
  lead,
}) => {
  const isPromoTypeEdit = !isNewPromo(id);
  const canEdit = useSelector((state: IStoreWithAuth) =>
    checkRight(state, USER_RIGHTS.PROMO_EDIT)
  );

  const disabled = isLeadPromo(location) || !canEdit;

  const dispatch = useDispatch();

  const { type, isLoading, validFrom } = useSelector(
    (state: IStoreWithRegistryState) => {
      const promoLoading = registrySelector<boolean>(
        state,
        PROMO_REGISTRIES.LOADING
      );
      const promoForm = formSelector(state, PROMO_FORM_NAME);

      const promoType = get(promoForm, ['model', 'type']);
      const $validFrom = get(promoForm, ['model', 'validFrom']);

      return {
        type: promoType,
        isLoading: promoLoading,
        validFrom: $validFrom,
      };
    }
  );

  useEffect(() => {
    if (id) {
      dispatch(promoActions.getData({ id, location, leadId: lead?.id }));
    }
  }, [id]);

  return (
    <>
      {isLoading ? (
        <PromoSkeleton />
      ) : (
        <Form name={PROMO_FORM_NAME}>
          {!canEdit && !isLeadPromo(location) && (
            <Spacing mb={15}>
              <ErrorMessageWrapper>
                You do not have the right to edit a promo.
              </ErrorMessageWrapper>
            </Spacing>
          )}
          <Spacing pr={6}>
            <Grid flow="row" columns={1} gap="0">
              <Grid columns="1fr 1fr">
                <Cell>
                  <Label htmlFor="name" isRequired>
                    Name
                  </Label>
                </Cell>
                <FormField
                  id="name"
                  shouldFitContainer
                  name="name"
                  component={InputFormField}
                  disabled={disabled}
                  errorProps={errorProps}
                />

                <Cell>
                  <Label htmlFor="comment" isRequired>
                    Internal Description
                  </Label>
                </Cell>
                <FormField
                  name="comment"
                  id="comment"
                  component={TextareaFormField}
                  disabled={disabled}
                  errorProps={errorProps}
                />

                <Cell>
                  <Label htmlFor="validFrom" isRequired>
                    Valid From
                  </Label>
                </Cell>
                <FormField
                  name="validFrom"
                  component={DatePickerFormField}
                  disabled={disabled}
                  minDate={new Date()}
                  errorProps={errorProps}
                  dateFormat={formDatePickerFormat}
                />
                <Cell>
                  <Label htmlFor="validFrom" isRequired>
                    Valid To
                  </Label>
                </Cell>
                <FormField
                  name="validTo"
                  minDate={validFrom ? new Date(validFrom) : undefined}
                  component={DatePickerFormField}
                  disabled={disabled}
                  errorProps={errorProps}
                  dateFormat={formDatePickerFormat}
                />
                <Cell>
                  <Label htmlFor="businessUnits" isRequired>
                    Business Units
                  </Label>
                </Cell>
                <FormField
                  inputId="businessUnits"
                  name="businessUnits"
                  dictionaryName="businessUnitsByEnviroments"
                  placeholder="Business Units"
                  shouldFitContainer
                  isMulti
                  isGrouped
                  openMenuOnFocus
                  menuPlacement="auto"
                  disabled={disabled}
                  component={DictionarySelect}
                  errorProps={errorProps}
                  hideSelectedOptions={false}
                  isSearchable
                />

                <Cell>
                  <Label htmlFor="visible" isRequired>
                    Visible
                  </Label>
                </Cell>
                <FormField
                  inputId="visible"
                  name="visible"
                  dictionaryName="visible"
                  placeholder="Visible"
                  openMenuOnFocus
                  shouldFitContainer
                  menuPlacement="auto"
                  component={DictionarySelect}
                  disabled={disabled}
                  errorProps={errorProps}
                />

                <BrandLink location={location} lead={lead} id={id} />

                <Cell>
                  <Label htmlFor="type" isRequired>
                    Type
                  </Label>
                </Cell>
                <FormField
                  name="type"
                  inputId="type"
                  openMenuOnFocus
                  dictionaryName="mappedTypes"
                  placeholder="Type"
                  shouldFitContainer
                  menuPlacement="auto"
                  disabled={isPromoTypeEdit || disabled}
                  component={DictionarySelect}
                  errorProps={errorProps}
                />
                <Cell>
                  <Label htmlFor="transactionSubType" isRequired>
                    Credit Sub Type
                  </Label>
                </Cell>
                <FormField
                  name="transactionSubType"
                  inputId="transactionSubType"
                  openMenuOnFocus
                  dictionaryName={TRANSACTION_SUB_TYPE_CREDIT_DICTIONARY_NAME}
                  placeholder="Credit Sub Type"
                  shouldFitContainer
                  menuPlacement="auto"
                  disabled={disabled}
                  component={DictionarySelect}
                  errorProps={errorProps}
                />
                {type === PROMO_TYPE.DEVALUATION_BONUS && (
                  <>
                    <Cell>
                      <Label htmlFor="depositCurrency" isRequired>
                        Deposit Currency
                      </Label>
                    </Cell>
                    <FormField
                      inputId="depositCurrency"
                      dictionaryName="depositCurrencies"
                      openMenuOnFocus
                      shouldFitContainer
                      name="rule.depositCurrency"
                      component={DictionarySelect}
                      disabled={disabled}
                      errorProps={errorProps}
                    />
                    <Cell>
                      <Label htmlFor="devaluationCurrency" isRequired>
                        Devaluation Currency
                      </Label>
                    </Cell>
                    <FormField
                      inputId="devaluationCurrency"
                      dictionaryName="devaluationCurrencies"
                      openMenuOnFocus
                      shouldFitContainer
                      name="rule.devaluationCurrency"
                      component={DictionarySelect}
                      disabled={disabled}
                      errorProps={errorProps}
                    />
                  </>
                )}
                <Cell>
                  <Label htmlFor="leadRetentionFilter" isRequired>
                    Is Fully Activated
                  </Label>
                </Cell>
                <FormField
                  inputId="leadRetentionFilter"
                  name="leadRetentionFilter"
                  dictionaryName="leadRetentionFilter"
                  placeholder="None"
                  openMenuOnFocus
                  shouldFitContainer
                  menuPlacement="auto"
                  component={DictionarySelect}
                  disabled={disabled}
                  errorProps={errorProps}
                />
              </Grid>
              <Limitation isNew={isNewPromo(id)} disabled={disabled} />
              <Rules disabled={disabled} />
              <Description disabled={disabled} />
              {!disabled && canEdit && (
                <PromoFormActions disabled={disabled} id={id} />
              )}
            </Grid>
          </Spacing>
        </Form>
      )}
    </>
  );
};
